import React from 'react';
import { motion } from 'motion/react';
import { useExperienceStatus } from './useExperienceStatus';
import { Icons } from '@/src/modules/components/Icons';

const stepLabels: Record<string, string> = {
  validate: 'Preparing your story',
  prompts: 'Designing your scenes',
  images: 'Painting your ceremonies',
  compile: 'Weaving it together',
  finalize: 'Applying finishing touches',
};

export function ExperienceProgress({
  invitationSlug,
  coupleNames,
}: {
  invitationSlug: string;
  coupleNames?: string;
}) {
  const { data, error } = useExperienceStatus(invitationSlug);

  if (error || data?.status === 'FAILED') {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6 text-center font-sans">
        <div className="max-w-md w-full flex flex-col items-center gap-6">
          <div className="w-16 h-16 rounded-full bg-red-50 text-red-500 flex items-center justify-center">
            <Icons.Warning size={32} />
          </div>
          <h2 className="text-2xl font-serif text-slate-800">We hit a snag</h2>
          <p className="text-slate-600">
            We're so sorry, but something went wrong while generating {coupleNames ? `${coupleNames}'s` : 'your'} invitation.
            Our team has been notified and we are looking into it.
          </p>
        </div>
      </div>
    );
  }

  const currentStepLabel = data ? (stepLabels[data.step] || 'Processing') : 'Warming up the studio';

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-6 text-center font-sans relative overflow-hidden">
      {/* Background soft gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-amber-100/30 rounded-full blur-3xl opacity-50 mix-blend-multiply" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-rose-100/30 rounded-full blur-3xl opacity-50 mix-blend-multiply" />

      <div className="relative z-10 max-w-md w-full flex flex-col items-center gap-10">
        <div className="relative">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
            className="absolute -inset-4 border border-amber-200/50 rounded-full border-t-amber-400/80"
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
            className="absolute -inset-8 border border-rose-200/50 rounded-full border-b-rose-400/80"
          />
          
          <div className="w-24 h-24 rounded-full bg-white shadow-xl shadow-amber-900/5 flex items-center justify-center relative overflow-hidden">
             <motion.div
                className="absolute inset-0 bg-gradient-to-tr from-amber-100 to-rose-50"
                animate={{
                  opacity: [0.5, 0.8, 0.5],
                  scale: [1, 1.05, 1],
                }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
             />
             <Icons.Sparkles className="w-8 h-8 text-amber-600 relative z-10" />
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <motion.h2 
            key={currentStepLabel}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-3xl font-serif text-slate-800"
          >
            {currentStepLabel}...
          </motion.h2>
          
          <p className="text-slate-500 font-medium tracking-wide">
            {coupleNames ? `Crafting ${coupleNames}'s cinematic experience.` : 'Crafting your cinematic experience.'}
          </p>
        </div>

        <div className="mt-8 px-6 py-4 bg-white/60 backdrop-blur-md rounded-2xl border border-white shadow-sm max-w-xs mx-auto">
          <p className="text-sm text-slate-500 leading-relaxed">
            This magical process takes a few minutes. Grab a coffee, we'll auto-refresh when it's ready.
          </p>
        </div>
      </div>
    </div>
  );
}
